import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Star, Phone, Mail, MapPin, Globe, Pencil, Trash2, Clapperboard,
  StickyNote, CalendarClock, Link2, Users, Building,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { getDirectoryContact, getDirectoryContactHistory, toggleDirectoryFavorite, deleteDirectoryContact } from "../../services/api";
import { displayName, initials } from "./directoryUtils";

const Row = ({ icon: Icon, children, href, testId }) => (
  <div className="flex items-start gap-2.5 text-sm" data-testid={testId}>
    <Icon size={15} className="text-[#26788A] mt-0.5 shrink-0" />
    {href ? <a href={href} className="text-[#26788A] hover:underline break-all">{children}</a>
      : <span className="text-slate-700 break-words">{children}</span>}
  </div>
);

export const ContactDetailDialog = ({ open, onClose, contactId, categories, onEdit, onChanged }) => {
  const [contact, setContact] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (open && contactId) {
      getDirectoryContact(contactId).then((r) => setContact(r.data)).catch(() => onClose());
      getDirectoryContactHistory(contactId).then((r) => setHistory(r.data)).catch(() => {});
    } else {
      setContact(null); setHistory([]);
    }
  }, [open, contactId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!contact) return null;
  const cat = categories.find((c) => c.id === contact.category_id);
  const isPlace = contact.kind === "place";

  const toggleFav = async () => {
    const res = await toggleDirectoryFavorite(contact.id);
    setContact({ ...contact, is_favorite: res.data.is_favorite });
    onChanged?.();
  };

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer définitivement « ${displayName(contact)} » du répertoire ?`)) return;
    try {
      await deleteDirectoryContact(contact.id);
      toast.success("Fiche supprimée");
      onChanged?.();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const fmtMonth = (d) => new Date(d + "T00:00:00").toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="contact-detail-dialog">
        <DialogHeader>
          <DialogTitle className="sr-only">{displayName(contact)}</DialogTitle>
        </DialogHeader>

        <div className="flex items-start gap-4">
          {contact.photo ? (
            <img src={contact.photo} alt="" className="w-20 h-20 rounded-xl object-cover border border-slate-200" />
          ) : (
            <div className="w-20 h-20 rounded-xl bg-[#26788A]/10 flex items-center justify-center">
              {isPlace ? <Building size={26} className="text-[#26788A]" /> :
                <span className="text-xl font-bold text-[#26788A]">{initials(contact)}</span>}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-slate-900 leading-tight" data-testid="detail-name">{displayName(contact)}</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {isPlace ? (contact.place_type || "Lieu") : (contact.job || "")}
              {cat && <span className="text-slate-400"> · {cat.name}</span>}
            </p>
            {(contact.tags || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {contact.tags.map((t) => (
                  <span key={t} className="text-[10px] font-medium bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{t}</span>
                ))}
              </div>
            )}
          </div>
          <button onClick={toggleFav} data-testid="detail-favorite-btn"
            className={`p-2 rounded-lg transition-colors ${contact.is_favorite ? "text-amber-500 bg-amber-50" : "text-slate-300 hover:text-amber-500 hover:bg-amber-50"}`}>
            <Star size={18} fill={contact.is_favorite ? "currentColor" : "none"} />
          </button>
        </div>

        <div className="space-y-2.5 mt-1">
          {contact.phone && <Row icon={Phone} href={`tel:${contact.phone.replace(/\s/g, "")}`} testId="detail-phone">{contact.phone}</Row>}
          {contact.email && <Row icon={Mail} href={`mailto:${contact.email}`} testId="detail-email">{contact.email}</Row>}
          {(contact.address || contact.city) && (
            <Row icon={MapPin} testId="detail-address">{[contact.address, contact.city].filter(Boolean).join(", ")}</Row>
          )}
          {contact.website && (
            <Row icon={Globe} href={contact.website.startsWith("http") ? contact.website : undefined} testId="detail-website">{contact.website}</Row>
          )}
          {contact.socials && <Row icon={Link2}>{contact.socials}</Row>}
          {isPlace && contact.on_site_contact && <Row icon={Users}>Sur place : {contact.on_site_contact}</Row>}
          {isPlace && contact.capacity_info && <Row icon={Building}>{contact.capacity_info}</Row>}
          {contact.availability && <Row icon={CalendarClock} testId="detail-availability">Disponibilités : {contact.availability}</Row>}
        </div>

        {isPlace && (contact.photos || []).length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {contact.photos.map((p, i) => (
              <img key={i} src={p} alt="" className="w-full h-24 object-cover rounded-lg border border-slate-200" />
            ))}
          </div>
        )}

        {isPlace && contact.constraints && (
          <div className="bg-amber-50 border border-amber-100 rounded-lg p-2.5 text-xs text-amber-800">
            <span className="font-semibold">Contraintes : </span>{contact.constraints}
          </div>
        )}

        {(contact.links || []).length > 0 && (
          <div className="space-y-1">
            {contact.links.map((l, i) => (
              <a key={i} href={l.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-[#26788A] hover:underline">
                <Link2 size={12} /> {l.label}
              </a>
            ))}
          </div>
        )}

        {contact.notes && (
          <div className="bg-slate-50 border border-slate-100 rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 flex items-center gap-1 mb-1"><StickyNote size={11} /> Notes internes</p>
            <p className="text-xs text-slate-600 whitespace-pre-wrap">{contact.notes}</p>
          </div>
        )}

        {/* Historique Studio Raneem */}
        <div data-testid="detail-history">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 flex items-center gap-1 mb-1.5">
            <Clapperboard size={11} /> Projets réalisés avec Studio Raneem
          </p>
          {history.length === 0 ? (
            <p className="text-xs text-slate-400">Aucun projet enregistré pour l'instant.</p>
          ) : (
            <div className="space-y-1">
              {history.map((ev) => (
                <div key={ev.id} className="flex items-center gap-2 text-xs">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: ev.color || "#26788A" }}></span>
                  <span className="font-medium text-slate-700">{ev.title}</span>
                  <span className="text-slate-400">— {fmtMonth(ev.date)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-between items-center pt-2 border-t border-slate-100">
          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600 hover:bg-red-50" onClick={handleDelete} data-testid="detail-delete-btn">
            <Trash2 size={14} className="mr-1" /> Supprimer
          </Button>
          <Button size="sm" className="bg-[#26788A] hover:bg-[#1D5E6C] text-white" onClick={() => onEdit(contact)} data-testid="detail-edit-btn">
            <Pencil size={13} className="mr-1.5" /> Modifier
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
